import { useState } from 'react';
import { authService } from '../services/authService';

export interface User {
  id: number;
  username: string;
  displayName?: string;
}

export interface LoginForm {
  username: string;
  password: string;
}

export interface RegisterForm {
  username: string;
  password: string;
  confirmPassword: string;
  couponCode: string;
  displayName: string;
}

export interface CouponValidation {
  isValid: boolean | null;
  message: string;
  monthsFree?: number;
  description?: string;
}

export interface UseAuthReturn {
  // Auth State
  isAuthenticated: boolean;
  user: User | null;
  authError: string;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  
  // Form State
  loginForm: LoginForm;
  setLoginForm: React.Dispatch<React.SetStateAction<LoginForm>>;
  registerForm: RegisterForm;
  setRegisterForm: React.Dispatch<React.SetStateAction<RegisterForm>>;
  couponValidation: CouponValidation;
  setCouponValidation: React.Dispatch<React.SetStateAction<CouponValidation>>;
  
  // Auth Actions
  checkAuthStatus: () => Promise<void>;
  handleLogin: (e: React.FormEvent, onSuccess?: () => void) => Promise<void>;
  handleRegister: (e: React.FormEvent, onSuccess?: () => void) => Promise<void>;
  handleLogout: () => Promise<void>;
  validateCoupon: (code: string) => Promise<void>;
  
  // Helper functions
  resetAuthError: () => void;
  resetForms: () => void;
}

export const useAuth = (): UseAuthReturn => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [authError, setAuthError] = useState('');
  
  // Form state
  const [loginForm, setLoginForm] = useState<LoginForm>({ 
    username: '', 
    password: '' 
  });
  
  const [registerForm, setRegisterForm] = useState<RegisterForm>({ 
    username: '', 
    password: '', 
    confirmPassword: '', 
    couponCode: '', 
    displayName: '' 
  });
  
  const [couponValidation, setCouponValidation] = useState<CouponValidation>({ 
    isValid: null, 
    message: '' 
  });

  const checkAuthStatus = async () => {
    const result = await authService.checkAuthStatus();
    setIsAuthenticated(result.authenticated);
    setUser(result.user || null);
  };

  const handleLogin = async (e: React.FormEvent, onSuccess?: () => void) => {
    e.preventDefault();
    setAuthError('');
    
    const result = await authService.login(loginForm);
    
    if (result.success) {
      setIsAuthenticated(true);
      setUser(result.user || null);
      setLoginForm({ username: '', password: '' });
      setAuthError('');
      onSuccess?.();
    } else {
      setAuthError(result.error || 'Login failed');
    }
  };

  const handleRegister = async (e: React.FormEvent, onSuccess?: () => void) => {
    e.preventDefault();
    setAuthError('');
    
    const result = await authService.register(registerForm, couponValidation);
    
    if (result.success) {
      // Automatically log in the user after successful registration
      setIsAuthenticated(true);
      setUser(result.user || null);
      setRegisterForm({ 
        username: '', 
        password: '', 
        confirmPassword: '', 
        couponCode: '', 
        displayName: '' 
      });
      setCouponValidation({ isValid: null, message: '' });
      setAuthError('');
      onSuccess?.();
    } else {
      setAuthError(result.error || 'Registration failed');
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  const validateCoupon = async (code: string) => {
    const result = await authService.validateCoupon(code);
    setCouponValidation(result);
  };

  const resetAuthError = () => setAuthError('');
  
  const resetForms = () => {
    setLoginForm({ username: '', password: '' });
    setRegisterForm({ 
      username: '', 
      password: '', 
      confirmPassword: '', 
      couponCode: '', 
      displayName: '' 
    });
    setCouponValidation({ isValid: null, message: '' });
    setAuthError('');
  };

  return {
    // Auth State
    isAuthenticated,
    user,
    authError,
    setUser,
    
    // Form State
    loginForm,
    setLoginForm,
    registerForm,
    setRegisterForm,
    couponValidation,
    setCouponValidation,
    
    // Auth Actions
    checkAuthStatus,
    handleLogin,
    handleRegister,
    handleLogout,
    validateCoupon,
    
    // Helper functions
    resetAuthError,
    resetForms,
  };
};